import { useCallback, useEffect, useRef, useState } from 'react';
import { getMemberId, getToken, wsUrl } from '../lib/api';
import type { Member, Role } from '../lib/api';

export interface ThreadMessage {
  seq: number;
  kind?: string;
  text: string;
  fromMemberId?: string | null;
}

export interface LocalEcho {
  clientMsgId: string;
  text: string;
}

export interface ThreadState {
  messages: ThreadMessage[];
  echoes: LocalEcho[];
  /** True while the socket is open — drives the header connection dot. */
  connected: boolean;
  /** True once the first history frame lands, so the skeleton has an end. */
  historyLoaded: boolean;
}

/**
 * Couple WebSocket: history, live messages, ack'd local echo, auto-reconnect.
 * Replaces the `connectWebSocket` / `renderHistory` / `appendMessage` /
 * `sendWsText` block from `main.ts`.
 *
 * Two deliberate changes:
 *  - messages are kept sorted by `seq`. The old code appended each older
 *    history page after the newer one, so a thread longer than one page
 *    rendered out of order.
 *  - rendering is declarative, so there is no `innerHTML` + `escapeHtml` pair
 *    to keep in sync — React escapes text nodes by default.
 */
export function useThread(opts: {
  token: string | null;
  members: Member[];
  onInvalidToken: () => void;
}) {
  const { token, members, onInvalidToken } = opts;
  const [state, setState] = useState<ThreadState>({ messages: [], echoes: [], connected: false, historyLoaded: false });
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef<number | null>(null);
  const seenRef = useRef<Set<number>>(new Set());
  const membersRef = useRef(members);
  membersRef.current = members;
  const invalidRef = useRef(onInvalidToken);
  invalidRef.current = onInvalidToken;

  const appendMessage = useCallback((msg: ThreadMessage) => {
    if (typeof msg.seq === 'number') {
      if (seenRef.current.has(msg.seq)) return;
      seenRef.current.add(msg.seq);
    }
    setState(s => ({ ...s, messages: [...s.messages, msg] }));
  }, []);

  const send = useCallback((text: string, wantsAgent = false) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const clientMsgId = crypto.randomUUID();
    setState(s => ({ ...s, echoes: [...s.echoes, { clientMsgId, text }] }));
    // wantsAgent carries the composer chip (invoke path, card hphiEFLMogbg).
    // Omitted entirely when false so the default frame stays byte-identical
    // to what shipped before.
    ws.send(
      JSON.stringify(
        wantsAgent
          ? { v: 1, t: 'send', clientMsgId, text, wantsAgent: true }
          : { v: 1, t: 'send', clientMsgId, text },
      ),
    );
  }, []);

  const reset = useCallback(() => {
    seenRef.current = new Set();
    setState({ messages: [], echoes: [], connected: false, historyLoaded: false });
  }, []);

  useEffect(() => {
    if (!token) return;

    let closed = false;
    const connect = () => {
      if (closed) return;
      if (wsRef.current && wsRef.current.readyState <= WebSocket.CONNECTING) return;
      const ws = new WebSocket(wsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ v: 1, t: 'hello', token }));
        setState(s => ({ ...s, connected: true }));
      };

      ws.onmessage = (ev: MessageEvent) => {
        let msg: any;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        switch (msg.t) {
          case 'welcome':
            ws.send(JSON.stringify({ v: 1, t: 'history', limit: 50 }));
            break;
          case 'history': {
            const page: ThreadMessage[] = msg.messages || [];
            setState(s => (s.historyLoaded ? s : { ...s, historyLoaded: true }));
            // Oldest pages are requested with `before:`, so merge then sort —
            // appending in arrival order renders old messages last.
            if (page.length) {
              seenRef.current = new Set([...seenRef.current, ...page.map(m => m.seq)]);
              setState(s => ({
                ...s,
                messages: [...s.messages, ...page].sort((a, b) => a.seq - b.seq),
              }));
            }
            if (msg.hasMore && page.length) {
              const oldest = page[0];
              if (oldest.seq > 1) ws.send(JSON.stringify({ v: 1, t: 'history', before: oldest.seq, limit: 50 }));
            }
            break;
          }
          case 'ack':
            setState(s => ({ ...s, echoes: s.echoes.filter(e => e.clientMsgId !== msg.clientMsgId) }));
            break;
          case 'msg':
            appendMessage(msg.message);
            break;
          case 'error':
            if (msg.code === 'bad_token') invalidRef.current();
            break;
        }
      };

      ws.onclose = () => {
        setState(s => ({ ...s, connected: false }));
        if (closed || !getToken()) return;
        retryRef.current = window.setTimeout(connect, 2000);
      };
    };

    connect();
    return () => {
      closed = true;
      if (retryRef.current) window.clearTimeout(retryRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      setState(s => ({ ...s, connected: false }));
    };
  }, [token, appendMessage]);

  return { ...state, send, reset, roleFor: roleForMember(members), myMemberId: getMemberId() };
}

function roleForMember(members: Member[]) {
  return (memberId: string | null | undefined): Role | null => {
    if (!memberId) return null;
    return members.find(m => m.memberId === memberId)?.role ?? null;
  };
}
